import { type PublicProposal, contractTotals } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Field, Loading, cx, formatCents } from "../components/ui";
import { SignaturePad } from "../components/signature-pad";
import { type Adopted, GuidedSigning } from "../components/guided-signing";

const CONSENT =
  "I agree to sign this proposal electronically. My electronic signature and initials are the legal equivalent of my handwritten signature, and I can download a signed copy.";

/** The customer's proposal page: no account, the link is the key. Review, sign, or decline. */
export function PublicProposalPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["public-proposal", token], queryFn: () => get<PublicProposal>(`/api/public/proposals/${token}`), retry: false });
  const set = (d: PublicProposal) => qc.setQueryData(["public-proposal", token], d);

  useEffect(() => {
    if (q.data?.company.primaryColor) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data?.company.primaryColor]);

  if (q.isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert>
      </div>
    );
  }
  const p = q.data;
  const guided = !!p.signing && p.status !== "signed" && p.status !== "declined" && !p.expired;
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-center gap-3">
            {p.company.logoUrl && <img src={p.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
            <span className="text-lg font-semibold">{p.company.name}</span>
          </div>
          {p.company.phone && (
            <a href={`tel:${p.company.phone}`} className="text-sm text-white/80 hover:text-white">
              {p.company.phone}
            </a>
          )}
        </div>
      </header>
      <main className="mx-auto flex max-w-4xl flex-col gap-5 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">{p.kind === "insurance" ? "Your insurance repairs proposal" : `Your proposal: ${p.title}${p.custom?.revision ? ` (Rev ${p.custom.revision})` : ""}`}</h1>
          <p className="text-ink-soft">
            For {p.customerName}
            {p.address && ` · ${p.address}`}
          </p>
        </div>
        <div className={p.kind !== "insurance" ? "grid gap-3" : "grid gap-3 sm:grid-cols-2"}>
          <div className="rounded-[var(--radius-panel)] bg-white px-5 py-4 shadow-[var(--shadow-card)]">
            <div className="text-xs font-semibold tracking-wide text-muted uppercase">{p.custom?.options.length && p.status !== "signed" ? "Base price" : "Contract total"}</div>
            <div className="text-2xl font-semibold">{formatCents(p.contractCents)}</div>
          </div>
          {p.kind === "insurance" && <div className="rounded-[var(--radius-panel)] bg-ink px-5 py-4 text-white">
            <div className="text-xs font-semibold tracking-wide uppercase opacity-75">Your estimated out of pocket</div>
            <div className="text-2xl font-semibold">{formatCents(p.outOfPocketCents)}</div>
          </div>}
        </div>
        {guided ? (
          <GuidedSign token={token} p={p} onDone={set} />
        ) : (
        <div className="overflow-hidden rounded-[var(--radius-panel)] bg-white shadow-[var(--shadow-card)]">
          <iframe src={p.signedPdfUrl ?? p.pdfUrl} title="Proposal" className="h-[70dvh] w-full" />
          <div className="border-t border-mist px-5 py-3 text-sm">
            <a href={p.signedPdfUrl ?? p.pdfUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
              Open the {p.signedPdfUrl ? "signed " : ""}proposal (PDF)
            </a>
          </div>
        </div>
        )}
        {guided ? null : p.status === "signed" ? (
          <Alert tone="ok">
            Signed by {p.signatureName} on {new Date(p.signedAt!).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}. Thank you! {p.repName} will be in touch to schedule your project.
          </Alert>
        ) : p.status === "declined" ? (
          <Alert tone="info">You declined this proposal. If you change your mind, call us at {p.company.phone}.</Alert>
        ) : p.expired ? (
          <Alert>This proposal has expired. Call {p.company.phone} for an updated one.</Alert>
        ) : (
          <SignForm token={token} p={p} onDone={set} />
        )}
        <p className="pb-6 text-center text-xs text-muted">
          {p.company.name}
          {p.company.email && ` · ${p.company.email}`}
        </p>
      </main>
    </div>
  );
}

function SignForm({ token, p, onDone }: { token: string; p: PublicProposal; onDone: (d: PublicProposal) => void }) {
  const [name, setName] = useState(p.customerName);
  const [initials, setInitials] = useState(
    p.customerName
      .split(/\s+/)
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase()
      .slice(0, 4),
  );
  const [selections, setSelections] = useState<string[]>(p.selections.map(() => ""));
  const [comments, setComments] = useState("");
  const custom = p.custom;
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [po, setPo] = useState("");
  const total = custom ? contractTotals({ ...custom.core, options: custom.options }, picked).contractCents : p.contractCents;
  const [consent, setConsent] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const pad = useRef<HTMLCanvasElement>(null);

  const sign = useMutation({
    mutationFn: () => post<PublicProposal>(`/api/public/proposals/${token}/sign`, {
        name,
        initials,
        consent,
        selections,
        comments,
        signature: pad.current!.toDataURL("image/png"),
        ...(custom ? { options: picked, signerTitle: title, signerCompany: company, poNumber: po } : {}),
      }),
    onSuccess: onDone,
  });
  const decline = useMutation({ mutationFn: () => post<PublicProposal>(`/api/public/proposals/${token}/decline`, { reason }), onSuccess: onDone });

  return (
    <section className="flex flex-col gap-4 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
      <h2 className="text-lg font-semibold">Sign the proposal</h2>
      {p.validThrough && <p className="text-sm text-muted">Valid through {new Date(`${p.validThrough}T12:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.</p>}
      {(sign.error || decline.error) && <Alert>{errorMessage(sign.error ?? decline.error)}</Alert>}
      {custom && custom.options.length > 0 && (
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Options (tick the ones you want)</legend>
          {custom.options.map((o) => (
            <label key={o.id} className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] px-2 py-2 hover:bg-mist">
              <input type="checkbox" className="mt-1 size-4 shrink-0 accent-[var(--color-brand)]" checked={picked.includes(o.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, o.id] : picked.filter((x) => x !== o.id))} />
              <span className="min-w-0 flex-1">
                <span className="font-medium">{o.label}:</span> {o.description}
              </span>
              <span className="font-semibold whitespace-nowrap">
                {o.kind === "deduct" ? "-" : "+"}
                {formatCents(o.amountCents)}
              </span>
            </label>
          ))}
          <div className="mt-1 flex justify-between rounded-[var(--radius-control)] bg-mist px-3 py-2 font-semibold">
            <span>Your contract total</span>
            <span>{formatCents(total)}</span>
          </div>
        </fieldset>
      )}
      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <Field label="Your full name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        <Field label="Initials" value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 8))} />
      </div>
      {custom?.commercial && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Your title" value={title} onChange={(e) => setTitle(e.target.value)} autoComplete="organization-title" />
          <Field label="Company" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
          <Field label="PO number (if any)" value={po} onChange={(e) => setPo(e.target.value)} />
        </div>
      )}
      {!custom?.commercial && custom && (
        <Field label="PO number (optional)" value={po} onChange={(e) => setPo(e.target.value)} />
      )}
      {p.selections.length > 0 && (
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Your product selections (optional; you can tell us later)</legend>
          {p.selections.map((label, i) => (
            <Field key={label} label={label} value={selections[i] ?? ""} onChange={(e) => setSelections(selections.map((v, k) => (k === i ? e.target.value : v)))} />
          ))}
        </fieldset>
      )}
      <Field label="Comments or notes (optional)" value={comments} onChange={(e) => setComments(e.target.value)} />
      <SignaturePad canvas={pad} onDrawn={setDrawn} />
      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[var(--color-brand)]" />
        <span>{CONSENT} Your initials go on each page that asks for them.</span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" busy={sign.isPending} disabled={!consent || !drawn || name.trim().length < 2 || !initials.trim()} onClick={() => sign.mutate()}>
          {custom ? `Sign for ${formatCents(total)}` : "Sign proposal"}
        </Button>
        <button className="text-sm text-muted hover:text-ink" onClick={() => setDeclining(!declining)}>
          Decline
        </button>
      </div>
      {declining && (
        <div className="flex flex-col gap-2 border-t border-mist pt-4">
          <Field label="Anything you'd like us to know? (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div>
            <Button busy={decline.isPending} onClick={() => decline.mutate()}>
              Decline this proposal
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


/**
 * Guided signing (like DocuSign): the proposal page by page; the customer taps each spot to initial,
 * then the signature box, then Finish. Options, selections, and the commercial details sit around it.
 */
function GuidedSign({ token, p, onDone }: { token: string; p: PublicProposal; onDone: (d: PublicProposal) => void }) {
  const qc = useQueryClient();
  const [adopted, setAdopted] = useState<Adopted | null>(null);
  const [selections, setSelections] = useState<string[]>(p.selections.map(() => ""));
  const [comments, setComments] = useState("");
  const custom = p.custom;
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [po, setPo] = useState("");
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const total = custom ? contractTotals({ ...custom.core, options: custom.options }, picked).contractCents : p.contractCents;
  const base = `/api/public/proposals/${token}`;

  const sign = useMutation({
    mutationFn: () =>
      post<PublicProposal>(`${base}/sign`, {
        name: adopted!.name,
        initials: adopted!.initials,
        consent: true,
        selections,
        comments,
        signature: adopted!.signaturePng,
        ...(custom ? { options: picked, signerTitle: title, signerCompany: company, poNumber: po } : {}),
      }),
    onSuccess: (d) => {
      onDone(d);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });
  const decline = useMutation({ mutationFn: () => post<PublicProposal>(`${base}/decline`, { reason }), onSuccess: onDone });

  return (
    <>
      <Alert tone="info">
        Tap each yellow <b>Initial</b> box as you read: every page, plus a few key sections. Then tap <b>Sign here</b> and <b>Finish</b>. {p.validThrough && `Valid through ${new Date(`${p.validThrough}T12:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`}
      </Alert>
      {custom && (custom.options.length > 0 || custom.commercial) && (
        <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
          {custom.options.length > 0 && (
            <fieldset className="flex flex-col gap-1">
              <legend className="mb-1 text-sm font-semibold text-ink-soft">Options (tick the ones you want)</legend>
              {custom.options.map((o) => (
                <label key={o.id} className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] px-2 py-2 hover:bg-mist">
                  <input type="checkbox" className="mt-1 size-4 shrink-0 accent-[var(--color-brand)]" checked={picked.includes(o.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, o.id] : picked.filter((x) => x !== o.id))} />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{o.label}:</span> {o.description}
                  </span>
                  <span className="font-semibold whitespace-nowrap">
                    {o.kind === "deduct" ? "-" : "+"}
                    {formatCents(o.amountCents)}
                  </span>
                </label>
              ))}
              <div className="mt-1 flex justify-between rounded-[var(--radius-control)] bg-mist px-3 py-2 font-semibold">
                <span>Your contract total</span>
                <span>{formatCents(total)}</span>
              </div>
            </fieldset>
          )}
          {custom.commercial && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Your title" value={title} onChange={(e) => setTitle(e.target.value)} autoComplete="organization-title" />
              <Field label="Company" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
              <Field label="PO number (if any)" value={po} onChange={(e) => setPo(e.target.value)} />
            </div>
          )}
        </section>
      )}
      <GuidedSigning
        documentUrl={`${base}/document.pdf`}
        tapUrl={`${base}/tap`}
        signing={p.signing!}
        adopted={adopted}
        onAdopt={setAdopted}
        defaultName={p.customerName}
        consent={CONSENT}
        onChange={(signing) => qc.setQueryData(["public-proposal", token], { ...p, signing })}
        finish={
          <div className="flex flex-col items-end gap-1">
            {sign.error && <span className="text-sm text-[var(--color-danger,#b91c1c)]">{errorMessage(sign.error)}</span>}
            <Button variant="primary" busy={sign.isPending} disabled={!adopted} onClick={() => sign.mutate()}>
              {custom ? `Finish: sign for ${formatCents(total)}` : "Finish and sign"}
            </Button>
          </div>
        }
      />
      <section className="flex flex-col gap-4 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
        {(sign.error || decline.error) && <Alert>{errorMessage(sign.error ?? decline.error)}</Alert>}
        {custom && !custom.commercial && <Field label="PO number (optional)" value={po} onChange={(e) => setPo(e.target.value)} />}
        {p.selections.length > 0 && (
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Your product selections (optional; you can tell us later)</legend>
            {p.selections.map((label, i) => (
              <Field key={label} label={label} value={selections[i] ?? ""} onChange={(e) => setSelections(selections.map((v, k) => (k === i ? e.target.value : v)))} />
            ))}
          </fieldset>
        )}
        <Field label="Comments or notes (optional)" value={comments} onChange={(e) => setComments(e.target.value)} />
        <div>
          <button className="text-sm text-muted hover:text-ink" onClick={() => setDeclining(!declining)}>
            Decline this proposal
          </button>
        </div>
        {declining && (
          <div className="flex flex-col gap-2 border-t border-mist pt-4">
            <Field label="Anything you'd like us to know? (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div>
              <Button busy={decline.isPending} onClick={() => decline.mutate()}>
                Decline this proposal
              </Button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
